import {
	IChatCommandWidgetMessage,
	IChatCommandWidgetIframeData,
	IChatMessagesOut,
	IChatEventFormMessage,
	MessageType,
	EventType,
} from '@enuan/chat__utils';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useMessageContext } from '../message.context';
import { useSendMessage } from 'src/hooks/core/messages.hooks';
import { useLogError } from 'src/components/ErrorBoundary/useLogError';
import classNames from 'classnames';
import { styled } from '@mui/material/styles';

export const IFrame = styled(
	({ className }: { className?: string }) => {
		const { message } =
			useMessageContext<
				IChatCommandWidgetMessage<IChatCommandWidgetIframeData>
			>();
		const {
			_data: {
				parameters: { data },
			},
			metadata: { id },
		} = message;

		const $hasHtml = 'html' in data && data.html;
		const $hasUrl = 'url' in data && data.url;

		const hasHtml = $hasHtml && !$hasUrl;
		const hasUrl = $hasUrl && !$hasHtml;

		const isValid = hasHtml || hasUrl;

		// const [iframeRef, setIframeRef] = useState<HTMLIFrameElement | null>(null);
		const iframeRef = useRef<HTMLIFrameElement>(null);

		const [height, setHeight] = useState<number>();

		const sendMessage = useSendMessage();
		const logError = useLogError('FormMessage', { message });
		const submitPostback = useCallback(
			(values: Record<string, any>) => {
				const event: IChatMessagesOut<IChatEventFormMessage> = {
					_type: MessageType.EVENT,
					_data: {
						name: EventType.POSTBACK,
						parameters: {
							payload: values,
							metadata: {
								command_id: id,
							},
						},
					},
				};

				return sendMessage(event, true).catch((error) => {
					logError('submit', { error, payload: { event } });
					return undefined;
				});
			},
			[id, sendMessage, logError],
		);
		const submitPostbackRef = useRef(submitPostback);
		submitPostbackRef.current = submitPostback;

		useLayoutEffect(() => {
			if (!iframeRef.current) return;

			const current = iframeRef.current;
			const postbackCallback = (event: MessageEvent) => {
				if (event.data.type !== 'postback') return;
				submitPostbackRef.current(event.data.payload).then((result) => {
					current.contentWindow?.postMessage({
						type: 'postback/result',
						payload: result,
						meta: event.data.payload,
					});
				});
			};
			const setHeightCallback = () => {
				const { scrollHeight } =
					current.contentWindow?.document.body.parentElement ?? {};
				setHeight(scrollHeight);
			};
			const loadCallback = (event: Event) => {
				setHeightCallback();
				current.contentWindow?.addEventListener('message', postbackCallback);
			};

			iframeRef.current.addEventListener('load', loadCallback);
			return () => {
				current.removeEventListener('load', loadCallback);
				current.contentWindow?.removeEventListener('message', postbackCallback);
			};
		}, []);

		if (!isValid) return null;

		return (
			<iframe
				className={classNames('message__iframe', className)}
				ref={iframeRef}
				title={data.id}
				srcDoc={hasHtml ? data.html : undefined}
				src={hasUrl ? data.url : undefined}
				height={height ? height + 'px' : undefined}
			/>
		);
	},
	{
		target: 'IFrame-root',
		label: 'IFrame',
	},
)({
	border: 0,
	width: '100%',
	borderRadius: 'inherit',
	overflow: 'hidden',
});
